export interface CustomizeFlowInputs {
  website: string;
  productWebsite: string;
  customerSlug: string;
}

export const ONBOARDING_COMPLETED_KEY = 'hasCompletedOnboarding';
export const CUSTOMIZE_FLOW_STORAGE_KEY = 'customize-flow-inputs';
export const WORKSPACE_SLUG_STORAGE_KEY = 'workspaceSlug';
export const DEFAULT_WORKSPACE_SLUG = 'customer-workspace';
export const WORKSPACE_APPEARANCE_MODE_PREFIX = 'workspace-appearance-mode:';
export const SETUP_TOKEN_STORAGE_PREFIX = 'customer-setup-token:';

const sanitizeSlugCandidate = (value: string): string => {
  return value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/-{2,}/g, '-')
    .replace(/^-+|-+$/g, '');
};

export const sanitizeWorkspaceSlug = (value: string | null | undefined): string => {
  if (!value) {
    return DEFAULT_WORKSPACE_SLUG;
  }

  const sanitized = sanitizeSlugCandidate(value);
  return sanitized || DEFAULT_WORKSPACE_SLUG;
};

export const sanitizeWorkspaceSlugInput = (value: string | null | undefined): string => {
  return sanitizeSlugCandidate(value || '');
};

export const deriveWorkspaceSlugFromWebsite = (website: string): string => {
  const trimmed = website.trim();
  if (!trimmed) {
    return DEFAULT_WORKSPACE_SLUG;
  }

  let hostname = trimmed;

  try {
    const normalizedUrl = /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
    hostname = new URL(normalizedUrl).hostname;
  } catch {
    hostname = trimmed;
  }

  const withoutWww = hostname.replace(/^www\./i, '');
  const sanitized = sanitizeSlugCandidate(withoutWww.replace(/\./g, '-'));
  return sanitized || DEFAULT_WORKSPACE_SLUG;
};

const COMMON_HOST_PREFIXES = new Set([
  'app',
  'blog',
  'dashboard',
  'login',
  'portal',
  'shop',
  'store',
  'www',
]);

const COMMON_SECOND_LEVEL_SUFFIXES = new Set([
  'ac',
  'co',
  'com',
  'edu',
  'gov',
  'net',
  'org',
]);

export const deriveCustomerSlugFromWebsite = (website: string): string => {
  const trimmed = website.trim();
  if (!trimmed) {
    return DEFAULT_WORKSPACE_SLUG;
  }

  let hostname = trimmed;

  try {
    const normalizedUrl = /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
    hostname = new URL(normalizedUrl).hostname;
  } catch {
    hostname = trimmed;
  }

  const labels = hostname
    .toLowerCase()
    .replace(/\.$/, '')
    .split('.')
    .filter(Boolean);

  if (labels.length > 2 && COMMON_HOST_PREFIXES.has(labels[0])) {
    labels.shift();
  }

  let candidate = labels[0] || trimmed;
  if (labels.length >= 3 && labels[labels.length - 1].length === 2 && COMMON_SECOND_LEVEL_SUFFIXES.has(labels[labels.length - 2])) {
    candidate = labels[labels.length - 3];
  } else if (labels.length >= 2) {
    candidate = labels[labels.length - 2];
  }

  return sanitizeWorkspaceSlug(candidate);
};

export const readCustomizeFlowInputsFromStorage = (): Partial<CustomizeFlowInputs> => {
  const raw = window.localStorage.getItem(CUSTOMIZE_FLOW_STORAGE_KEY);
  if (!raw) {
    return {};
  }

  try {
    const parsed = JSON.parse(raw) as Partial<CustomizeFlowInputs>;
    return {
      website: typeof parsed.website === 'string' ? parsed.website : '',
      productWebsite: typeof parsed.productWebsite === 'string' ? parsed.productWebsite : '',
      customerSlug: typeof parsed.customerSlug === 'string' ? sanitizeWorkspaceSlug(parsed.customerSlug) : '',
    };
  } catch {
    return {};
  }
};

export const hasCompletedOnboarding = (): boolean => {
  return window.localStorage.getItem(ONBOARDING_COMPLETED_KEY) === 'true';
};

export const markOnboardingCompleted = (): void => {
  window.localStorage.setItem(ONBOARDING_COMPLETED_KEY, 'true');
};

export const persistWorkspaceSlug = (slug: string): string => {
  const normalizedSlug = sanitizeWorkspaceSlug(slug);
  window.localStorage.setItem(WORKSPACE_SLUG_STORAGE_KEY, normalizedSlug);
  return normalizedSlug;
};

export const resolveWorkspaceSlug = (website?: string): string => {
  if (website && website.trim()) {
    const websiteSlug = deriveWorkspaceSlugFromWebsite(website);
    if (websiteSlug !== DEFAULT_WORKSPACE_SLUG) {
      return websiteSlug;
    }
  }

  const storedSlug = window.localStorage.getItem(WORKSPACE_SLUG_STORAGE_KEY);
  if (storedSlug) {
    return sanitizeWorkspaceSlug(storedSlug);
  }

  const storedInputs = readCustomizeFlowInputsFromStorage();
  if (storedInputs.website) {
    return deriveWorkspaceSlugFromWebsite(storedInputs.website);
  }

  return DEFAULT_WORKSPACE_SLUG;
};

export type WorkspaceAppearanceMode = 'default' | 'brand';

export const readWorkspaceAppearanceMode = (slug: string): WorkspaceAppearanceMode => {
  const normalizedSlug = sanitizeWorkspaceSlug(slug);
  const raw = window.localStorage.getItem(`${WORKSPACE_APPEARANCE_MODE_PREFIX}${normalizedSlug}`);
  return raw === 'default' ? 'default' : 'brand';
};

export const persistWorkspaceAppearanceMode = (
  slug: string,
  mode: WorkspaceAppearanceMode,
): WorkspaceAppearanceMode => {
  const normalizedSlug = sanitizeWorkspaceSlug(slug);
  window.localStorage.setItem(`${WORKSPACE_APPEARANCE_MODE_PREFIX}${normalizedSlug}`, mode);
  return mode;
};

export const persistSetupToken = (slug: string, token: string | null | undefined): void => {
  const normalizedSlug = sanitizeWorkspaceSlug(slug);
  if (!token) {
    window.sessionStorage.removeItem(`${SETUP_TOKEN_STORAGE_PREFIX}${normalizedSlug}`);
    return;
  }
  window.sessionStorage.setItem(`${SETUP_TOKEN_STORAGE_PREFIX}${normalizedSlug}`, token);
};

export const readSetupToken = (slug: string): string | null => {
  const normalizedSlug = sanitizeWorkspaceSlug(slug);
  return window.sessionStorage.getItem(`${SETUP_TOKEN_STORAGE_PREFIX}${normalizedSlug}`);
};

export const setupOrAdminAuthHeaders = (slug: string): Record<string, string> => {
  const adminToken = window.sessionStorage.getItem('admin_token');
  const setupToken = readSetupToken(slug);
  return {
    'Content-Type': 'application/json',
    ...(adminToken ? { Authorization: `Bearer ${adminToken}` } : setupToken ? { Authorization: `Bearer ${setupToken}` } : {}),
  };
};
